import { get } from 'lodash/fp'
import Head from 'next/head'
import qs from 'qs'
import { useEffect } from 'react'
import { SearchResults, SearchState } from 'react-instantsearch-core'

import { serverSideConfigs, t } from 'localization'
import { SearchFiltersRefinement } from 'search'
import Grid, { GridConfig } from 'shared/components/Grid'
import { getResultsState, urlToSearchState } from 'shared/utils/algolia-utils'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'

const TP = 'pages.catalogsearch.result'

interface ISearchPageProps {
  resultsState: SearchResults
  initialSearchState: SearchState
  trendingResultsState: SearchResults
}

export const SearchPage = ({
  resultsState,
  initialSearchState,
  trendingResultsState,
}: ISearchPageProps) => {
  const hiddenFilters: Set<string> = new Set()
  // currently presentation sizes are only used for apparel
  hiddenFilters.add(SearchFiltersRefinement.PresentationSizes)
  const config: GridConfig = {
    context: { hiddenFilters, hasNewProductOnly: false },
    regex: new RegExp(`/catalogsearch/result`),
    indexPath: '/catalogsearch/result',
  }
  const query = get('query', initialSearchState) || ''

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [query])

  const searchTerm = query === '' ? t(`${TP}.all`, 'All') : query
  const title = t(`${TP}.searchResult`, {
    defaultValue: 'Search results for "{searchTerm}"',
    searchTerm,
  })

  return (
    <>
      <Head>
        <title>
          {t(`${TP}.pageTitle`, {
            defaultValue: '{title} | Flight Club',
            title,
          })}
        </title>
      </Head>
      <Grid
        title={title}
        config={config}
        resultsState={resultsState}
        initialSearchState={initialSearchState}
        noResultsState={trendingResultsState}
      />
    </>
  )
}
export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, req, locale } = context
  const asPath = req.url || ''
  const initialSearchState = urlToSearchState(
    asPath?.includes('?') ? qs.parse(asPath.substring(asPath.indexOf('?') + 1)) : {},
  )

  const { resultsState, trendingResultsState } = await getResultsState(initialSearchState)

  return {
    props: {
      ...(await getServerPageProps(context)),
      ...JSON.parse(
        JSON.stringify({
          ...(await serverSideConfigs(res, locale)),
          resultsState,
          initialSearchState,
          trendingResultsState,
        }),
      ),
    },
  }
}

export default SearchPage
